import { FC, useCallback, useContext } from 'react';
import { Form } from 'react-final-form';
import {
  AccommodationFieldsFragment,
  EditAccommodationLocationDocument,
  TimelineDocument,
} from '@flashpack/graphql';
import { Stack, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { FreeTextFormField } from 'design-system';
import { useMutation } from '@apollo/client';
import { useToast } from '@src/shared/toast/useToast';
import { useSafeMutation } from '@src/shared/useSafeMutation';
import { Decimal } from 'decimal.js';
import { MapLocationField } from '@src/shared/map/MapLocationField';
import { TimelineContext } from '@src/shared/timeline/TimelineContext';

interface PropTypes {
  accommodation: AccommodationFieldsFragment;
}

type Coordinates = {
  latitude: string;
  longitude: string;
};

interface LocationFormState {
  name: string;
  mapLocation: Coordinates;
}

export const AccommodationLocationTab: FC<PropTypes> = ({ accommodation }) => {
  const { success: successToast, error: errorToast } = useToast();
  const { safeMutation } = useSafeMutation();
  const [editLocation] = useMutation(EditAccommodationLocationDocument);
  const { timelineId } = useContext(TimelineContext);

  const initialValues = {
    name: accommodation.location?.name ?? '',
    latitude: accommodation.location?.latitude?.toString() ?? '',
    longitude: accommodation.location?.longitude?.toString() ?? '',
  };

  const handleSubmit = useCallback(
    async (formData: LocationFormState) => {
      await safeMutation(
        editLocation({
          variables: {
            input: {
              id: accommodation.id,
              location: {
                name: formData.name,
                latitude: new Decimal(formData.mapLocation.latitude),
                longitude: new Decimal(formData.mapLocation.longitude),
              },
            },
          },
          refetchQueries: [
            {
              query: TimelineDocument,
              variables: { id: timelineId },
            },
          ],
        }),
        {
          onSuccess: () => {
            successToast('Location successfully updated');
          },
          onError: () => {
            errorToast(
              'There was an issue while updating the location - try again later',
            );
          },
        },
      );
    },
    [accommodation.id, editLocation, safeMutation, successToast, errorToast, timelineId],
  );

  const validate = (values: LocationFormState) => {
    const errors: {
      name?: string;
      latitude?: string;
      longitude?: string;
    } = {};

    if (!values.name) {
      errors.name = 'Name is required';
    }
    return errors;
  };

  return (
    <Stack spacing={3}>
      <Typography variant="Body M" color="text.secondary">
        This is the location pin that will be displayed on the itinerary map on the
        website.
      </Typography>
      <Form
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validate={validate}
        render={({ handleSubmit, submitting, pristine, valid }) => (
          <form onSubmit={(form) => void handleSubmit(form)}>
            <Stack spacing={3}>
              <Stack spacing={1}>
                <Typography sx={{ color: 'principal.grey70' }}>Location Name</Typography>
                <FreeTextFormField
                  name="name"
                  placeholder="Enter location name"
                  required
                  data-testid="location-name-input"
                />
              </Stack>

              <MapLocationField
                name="mapLocation"
                label="Map Location"
                key={`map-location-${accommodation.id}`}
                required
                initialLocation={{
                  latitude: accommodation.location?.latitude?.toString() ?? '',
                  longitude: accommodation.location?.longitude?.toString() ?? '',
                }}
              />

              <Stack direction="row" justifyContent="flex-end">
                <LoadingButton
                  loading={submitting}
                  disabled={pristine || submitting || !valid}
                  type="submit"
                  variant="contained"
                  color="success"
                  data-testid="location-save-button"
                >
                  Save Changes
                </LoadingButton>
              </Stack>
            </Stack>
          </form>
        )}
      />
    </Stack>
  );
};
